import Cookies from 'universal-cookie'

import { REGISTRATION_QUERY_PARAMS } from '@lyrahealth-inc/shared-app-logic/src/features/registration/constants'
import type { PendingSsoIdentity } from '@lyrahealth-inc/shared-app-logic/src/models/login/Login'

import { getParentDomain } from './cookieUtils'
import { PARTNER_SSO_REGISTRATION_DATA_COOKIE, PENDING_SSO_INTERSTITIAL_COOKIE } from '../constants/appConstants'

const SSO_COOKIE_MAX_AGE_SECONDS = 60 * 15
const PENDING_SSO_INTERSTITIAL_COOKIE_MAX_AGE_SECONDS = 60

// Shared by every cookie set and removed here, so a removal always matches the cookie it targets
const parentDomainCookieOptions = () => ({ path: '/', domain: getParentDomain() })

const setParentDomainCookie = (name: string, value: string, maxAge: number) => {
  new Cookies().set(name, value, { ...parentDomainCookieOptions(), maxAge, secure: true, sameSite: 'lax' })
}

export type SsoRegistrationData = Partial<
  Pick<
    PendingSsoIdentity,
    | 'givenName'
    | 'familyName'
    | 'dob'
    | 'ssoRegistrationVerificationKey'
    | 'partnerIdpShortName'
    | 'ssoRedirectPath'
    | 'externalUserId'
    | 'connectedServiceType'
  >
> & { email: string }

/**
 * Build query parameters for navigating to the SSO registration form from the SSO interstitial.
 */
export function buildSsoRegistrationParams(data: SsoRegistrationData): Record<string, string> {
  return {
    [REGISTRATION_QUERY_PARAMS.ID]: data.email,
    [REGISTRATION_QUERY_PARAMS.IS_SSO]: 'true',
    ...(data.ssoRegistrationVerificationKey && {
      [REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_VERIFICATION_KEY_QUERY_PARAM]:
        data.ssoRegistrationVerificationKey,
    }),
    ...(data.partnerIdpShortName && {
      [REGISTRATION_QUERY_PARAMS.PARTNER_SSO_REGISTRATION_PARTNER_IDP_QUERY_PARAM]: data.partnerIdpShortName,
    }),
    ...(data.ssoRedirectPath && {
      [REGISTRATION_QUERY_PARAMS.REDIRECT]: data.ssoRedirectPath,
    }),
  }
}

/**
 * Set the cross-subdomain cookie with SSO user data (name, dob, connected service)
 * so the registration form can pre-fill fields.
 */
export function setSsoRegistrationDataCookie(data: SsoRegistrationData): void {
  if (!data.givenName && !data.familyName && !data.dob && !data.externalUserId && !data.connectedServiceType) {
    return
  }
  const cookieData = {
    firstName: data.givenName || '',
    lastName: data.familyName || '',
    ...(data.dob && { dob: data.dob }),
    ...(data.externalUserId && { externalUserId: data.externalUserId }),
    ...(data.connectedServiceType && { connectedServiceType: data.connectedServiceType }),
  }
  setParentDomainCookie(PARTNER_SSO_REGISTRATION_DATA_COOKIE, JSON.stringify(cookieData), SSO_COOKIE_MAX_AGE_SECONDS)
}

/**
 * Hand the pending SSO identity to the customer microsite, where Redux state doesn't carry over. The whole identity
 * is included: the interstitial page pre-fills from it and needs the verification key to build registration params.
 */
export function setPendingSsoInterstitialCookie(identity: PendingSsoIdentity): void {
  setParentDomainCookie(
    PENDING_SSO_INTERSTITIAL_COOKIE,
    JSON.stringify(identity),
    PENDING_SSO_INTERSTITIAL_COOKIE_MAX_AGE_SECONDS,
  )
}

export function getPendingSsoInterstitialCookie(): PendingSsoIdentity | null {
  return (new Cookies().get(PENDING_SSO_INTERSTITIAL_COOKIE) as PendingSsoIdentity | undefined) ?? null
}

export function clearPendingSsoInterstitialCookie(): void {
  new Cookies().remove(PENDING_SSO_INTERSTITIAL_COOKIE, parentDomainCookieOptions())
}
